import {
  Box,
  Card,
  Container,
  Grid,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import React, { useContext, useEffect, useState } from 'react';
import { I_S3Credentials } from 'src/interfaces/S3Types';
import { getChartOptions } from './chartOptions';
import {
  chartAggregatedDataInterface,
  chartDataInterface,
  overviewInterface,
  transformInputToAggregatedDataJson,
  transformInputToDailyDataJson
} from 'src/interfaces/Chart';
import Button from '@mui/material/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { UserContext } from '../../../contexts/userContext';
import { UserType } from '../../../interfaces/UserTypes';
import { TimeSpan, UnixTime } from '../../../dataCache/time';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';

interface OverviewProps {
  s3Credentials: I_S3Credentials;
  id: number;
  device?: number;
  product?: number;
  connected?: boolean;
  loading?: boolean;
}

const computeLast7Days = (): string[] => {
  const currentDate = new Date();
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  let currentDayIndex = currentDate.getDay();
  const last7Days = [];

  for (let i = 0; i < 7; i++) {
    last7Days.push(daysOfWeek[currentDayIndex]);
    currentDayIndex++;
    if (currentDayIndex > 6) {
      currentDayIndex = 0;
    }
  }
  return last7Days;
};

function Overview(props: OverviewProps) {
  const intl = useIntl();
  const context = useContext(UserContext);
  const { currentUser } = context;
  const [dailyData, setDailyData] = useState(true);
  const [aggregatedData, setAggregatedData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chartState, setChartState] = useState(0);
  const [aggregatedChartState, setAggregatedChartState] = useState(0);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isErrorDateModalOpen, setErrorDateModalOpen] = useState(false);
  const [dateSelectionError, setDateSelectionError] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [dailyDataArray, setDailyDataArray] = useState<
    {
      chartData: chartDataInterface;
      chartOverview: overviewInterface;
    }[]
  >([]);

  //console.log(dailyData);

  const [aggregatedDataArray, setAggregatedDataArray] = useState<
    {
      chartData: chartAggregatedDataInterface;
      chartOverview: overviewInterface;
      datelist: any[];
      netbalance: any[];
    }[]
  >([]);

  const [startDate, setStartDate] = useState(dayjs().add(-1, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [isZooming, setIsZooming] = useState(false);

  const { product } = useContext(ProductIdContext);

  // console.log(
  //   UnixTime.fromTicks(new Date().getTime() / 1000).earlier(
  //     TimeSpan.fromDays(1)
  //   )
  // );
  //
  // console.log(UnixTime.fromTicks(new Date().getTime() / 1000));
  useEffect(() => {
    if (isZooming) {
      setLoading(true);
    } else if (!isZooming && dailyDataArray.length > 0) {
      setLoading(false);
    }
  }, [isZooming, dailyDataArray]);

  useEffect(() => {
    if (props.connected === false) {
      setErrorDateModalOpen(false);
    }
  }, [props.connected]);

  useEffect(() => {
    const resultPromise: Promise<{
      chartData: chartDataInterface;
      chartOverview: overviewInterface;
    }> = transformInputToDailyDataJson(
      product,
      props.s3Credentials,
      props.id,
      UnixTime.fromTicks(new Date().getTime() / 1000).earlier(
        TimeSpan.fromDays(1)
      ),
      UnixTime.fromTicks(new Date().getTime() / 1000)
    );

    resultPromise
      .then((result) => {
        if (result.chartData.soc.data.length === 0) {
          setLoading(false);
          return;
        }
        setDailyDataArray((prevData) =>
          prevData.concat({
            chartData: result.chartData,
            chartOverview: result.chartOverview
          })
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }, []);

  const startZoom = () => {
    setIsZooming(true);
  };

  const handleBeforeZoom = (chartContext, { xaxis }) => {
    const startX = parseInt(xaxis.min) / 1000;
    const endX = parseInt(xaxis.max) / 1000;

    const resultPromise: Promise<{
      chartData: chartDataInterface;
      chartOverview: overviewInterface;
    }> = transformInputToDailyDataJson(
      product,
      props.s3Credentials,
      props.id,
      UnixTime.fromTicks(startX).earlier(TimeSpan.fromHours(2)),
      UnixTime.fromTicks(endX).earlier(TimeSpan.fromHours(2))
    );

    let isComponentMounted = true;

    resultPromise
      .then((result) => {
        if (isComponentMounted) {
          setDailyDataArray((prevData) =>
            prevData.concat({
              chartData: result.chartData,
              chartOverview: result.chartOverview
            })
          );
          setIsZooming(false);
          setChartState(dailyDataArray.length);
        }
      })
      .catch((error) => {
        if (isComponentMounted) {
          console.error('Error:', error);
          setLoading(false); // Ensure loading is turned off even if there is an error
        }
      });

    return () => {
      isComponentMounted = false;
    };
  };

  const handle24HourData = () => {
    setDailyData(true);
    setAggregatedData(false);
    setChartState(0);
  };

  const handleWeekData = () => {
    setDailyData(false);
    setAggregatedData(true);
    setAggregatedChartState(0);

    if (
      aggregatedDataArray[aggregatedChartState] &&
      aggregatedDataArray[aggregatedChartState].chartData != null
    ) {
      return;
    }
    setLoading(true);

    const resultPromise: Promise<{
      chartAggregatedData: chartAggregatedDataInterface;
      chartOverview: overviewInterface;
      dateList: string[];
    }> = transformInputToAggregatedDataJson(
      props.s3Credentials,
      dayjs().subtract(1, 'week'),
      dayjs(),
      product
    );

    resultPromise
      .then((result) => {
        if (result.dateList.length === 0) {
          setDateSelectionError(intl.formatMessage({ id: 'noDataForDateRange' }));
          setErrorDateModalOpen(true);
          setLoading(false);
          return;
        }

        const powerDifference = [];
        for (
          let i = 0;
          i < result.chartAggregatedData.gridImportPower.data.length;
          i++
        ) {
          powerDifference.push(
            result.chartAggregatedData.gridImportPower.data[i] -
              Math.abs(result.chartAggregatedData.gridExportPower.data[i])
          );
        }

        setAggregatedDataArray((prevData) =>
          prevData.concat({
            chartData: result.chartAggregatedData,
            chartOverview: result.chartOverview,
            datelist: result.dateList,
            netbalance: powerDifference
          })
        );

        setAggregatedChartState(aggregatedDataArray.length);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const handleSetDate = () => {
    setDateOpen(true);
    setIsDateModalOpen(true);
  };

  const handleOkOnErrorDateModal = () => {
    setErrorDateModalOpen(false);
  };

  const handleCancel = () => {
    setIsDateModalOpen(false);
    setDateOpen(false);
  };

  const handleConfirm = () => {
    setIsDateModalOpen(false);
    setDateOpen(false);

    if (endDate.isAfter(dayjs())) {
      setDateSelectionError('You cannot ask for future data');
      setErrorDateModalOpen(true);
      return;
    } else if (startDate.isAfter(endDate)) {
      setDateSelectionError('Εnd date must precede start date');
      setErrorDateModalOpen(true);
      return;
    }

    setLoading(true);

    if (dailyData) {
      const resultPromise: Promise<{
        chartData: chartDataInterface;
        chartOverview: overviewInterface;
      }> = transformInputToDailyDataJson(
        product,
        props.s3Credentials,
        props.id,
        UnixTime.fromTicks(startDate.unix()),
        UnixTime.fromTicks(endDate.unix())
      );

      resultPromise
        .then((result) => {
          if (result.chartData.soc.data.length === 0) {
            setDateSelectionError(intl.formatMessage({ id: 'noDataForDateRange' }));
            setErrorDateModalOpen(true);
            setLoading(false);
            return;
          }
          setDailyDataArray((prevData) =>
            prevData.concat({
              chartData: result.chartData,
              chartOverview: result.chartOverview
            })
          );

          setLoading(false);
          setChartState(dailyDataArray.length);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    } else {
      setDailyData(false);
      setAggregatedData(true);

      setLoading(true);

      const resultPromise: Promise<{
        chartAggregatedData: chartAggregatedDataInterface;
        chartOverview: overviewInterface;
        dateList: string[];
      }> = transformInputToAggregatedDataJson(
        props.s3Credentials,
        startDate,
        endDate,
        product
      );

      resultPromise
        .then((result) => {
          if (result.dateList.length === 0) {
            setDateSelectionError(intl.formatMessage({ id: 'noDataForDateRange' }));
            setErrorDateModalOpen(true);
            setLoading(false);
            return;
          }

          const powerDifference = [];

          for (
            let i = 0;
            i < result.chartAggregatedData.gridImportPower.data.length;
            i++
          ) {
            powerDifference.push(
              result.chartAggregatedData.gridImportPower.data[i] -
                Math.abs(result.chartAggregatedData.gridExportPower.data[i])
            );
          }

          setAggregatedDataArray((prevData) =>
            prevData.concat({
              chartData: result.chartAggregatedData,
              chartOverview: result.chartOverview,
              datelist: result.dateList,
              netbalance: powerDifference
            })
          );

          setAggregatedChartState(aggregatedDataArray.length);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  };

  const handleGoBack = () => {
    if (dailyData && chartState > 0) {
      setChartState(chartState - 1);
    }
    if (aggregatedData && aggregatedChartState > 0) {
      setAggregatedChartState(aggregatedChartState - 1);
    }
  };

  const handleGoForward = () => {
    if (dailyData && chartState + 1 < dailyDataArray.length) {
      setChartState(chartState + 1);
    }
    if (
      aggregatedData &&
      aggregatedChartState + 1 < aggregatedDataArray.length
    ) {
      setAggregatedChartState(aggregatedChartState + 1);
    }
  };

  const renderGraphs = () => {
    return (
      <Container maxWidth="xl">
        {!props.connected && !props.loading && (
          <Typography
            variant="body2"
            sx={{
              color: 'red',
              fontWeight: 'bold',
              textAlign: 'center',
              marginTop: '10px',
              marginBottom: '10px'
            }}
          >
            <FormattedMessage
              id="installationOffline"
              defaultMessage="Installation is currently offline. Showing last available data."
            />
          </Typography>
        )}
        {isErrorDateModalOpen && (
          <Modal open={isErrorDateModalOpen} onClose={() => {}}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 450,
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {dateSelectionError}
              </Typography>

              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handleOkOnErrorDateModal}
              >
                Ok
              </Button>
            </Box>
          </Modal>
        )}

        {isDateModalOpen && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Modal open={isDateModalOpen} onClose={() => {}}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 450,
                  bgcolor: 'background.paper',
                  borderRadius: 4,
                  boxShadow: 24,
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                <DateTimePicker
                  label="Select Start Date"
                  value={startDate}
                  onChange={(newDate) => setStartDate(newDate)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        marginTop: 2, // Apply styles here
                        width: '100%' // Optional: You can adjust the width or other styling here
                      }}
                    />
                  )}
                />

                <DateTimePicker
                  label="Select End Date"
                  value={endDate}
                  onChange={(newDate) => setEndDate(newDate)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        marginTop: 2, // Apply styles here
                        width: '100%' // Optional: You can adjust the width or other styling here
                      }}
                    />
                  )}
                />

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 10
                  }}
                >
                  <Button
                    sx={{
                      marginTop: 2,
                      textTransform: 'none',
                      bgcolor: '#ffc04d',
                      color: '#111111',
                      '&:hover': { bgcolor: '#f7b34d' }
                    }}
                    onClick={handleConfirm}
                  >
                    Confirm
                  </Button>

                  <Button
                    sx={{
                      marginTop: 2,
                      marginLeft: 2,
                      textTransform: 'none',
                      bgcolor: '#ffc04d',
                      color: '#111111',
                      '&:hover': { bgcolor: '#f7b34d' }
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Box>
            </Modal>
          </LocalizationProvider>
        )}
        <Grid container>
          {!props.loading && (props.connected !== false || dailyDataArray.length > 0 || aggregatedDataArray.length > 0) && (<>
          <Grid item xs={6} md={6}>
            <Button
              variant="contained"
              onClick={handle24HourData}
              disabled={loading}
              sx={{
                marginTop: '20px',
                backgroundColor: dailyData ? '#808080' : '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="24_hours" defaultMessage="24-hours" />
            </Button>
            {props.device !== 3 && props.product !== 2 && (
            <Button
              variant="contained"
              onClick={handleWeekData}
              disabled={loading}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: aggregatedData ? '#808080' : '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="lastweek" defaultMessage="Last week" />
            </Button>
            )}

            <Button
              variant="contained"
              onClick={handleSetDate}
              disabled={loading}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: dateOpen ? '#808080' : '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="set_date" defaultMessage="Set Date" />
            </Button>
          </Grid>

          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            item
            xs={6}
            md={6}
          >
            <Button
              variant="contained"
              disabled={
                dailyData ? !(chartState > 0) : !(aggregatedChartState > 0)
              }
              onClick={handleGoBack}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="goback" defaultMessage="Zoom out" />
            </Button>

            <Button
              variant="contained"
              disabled={
                dailyData
                  ? !(chartState < dailyDataArray.length - 1)
                  : !(aggregatedChartState < aggregatedDataArray.length - 1)
              }
              onClick={handleGoForward}
              sx={{
                marginTop: '20px',
                marginLeft: '10px',
                backgroundColor: '#ffc04d',
                color: '#000000',
                '&:hover': { bgcolor: '#f7b34d' }
              }}
            >
              <FormattedMessage id="goback" defaultMessage="Zoom in" />
            </Button>
          </Grid>
          </>)}

          {loading && (
            <Container
              maxWidth="xl"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
              }}
            >
              <CircularProgress size={60} style={{ color: '#ffc04d' }} />
              <Typography variant="body2" style={{ color: 'black' }} mt={2}>
                <FormattedMessage id="fetchingData" defaultMessage="Fetching data..." />
              </Typography>
            </Container>
          )}

          {!loading && dailyDataArray.length > 0 && (
            <Grid item xs={12} md={12}>
              {dailyData && (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={3}
                >
                  <Grid item md={12} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="overview_graph"
                                defaultMessage="Overview Graph"
                              />
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: 3
                          }}
                        ></Box>
                      </Box>

                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview.overview,
                            'dailyoverview',
                            [],
                            true,
                            (product === 2 || product === 5) ? dailyDataArray[chartState].chartOverview.batteryVoltage : undefined
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.gridPower,
                            type: 'line',
                            color: '#b30000'
                          },
                          {
                            ...dailyDataArray[chartState].chartData
                              .pvProduction,
                            type: 'line',
                            color: '#ff9900'
                          },
                          {
                            ...dailyDataArray[chartState].chartData.ACLoad,
                            type: 'line',
                            color: '#2ecc71'
                          },
                          {
                            ...dailyDataArray[chartState].chartData.soc,
                            type: 'line',
                            color: '#008FFB'
                          },
                          ...((product === 2 || product === 5) ? [{
                            ...dailyDataArray[chartState].chartData.batteryVoltage,
                            type: 'line' as const,
                            color: '#9b59b6'
                          }] : [])
                        ]}
                        height={420}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )}

              {aggregatedData && (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={3}
                >
                  <Grid item md={12} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="overview_graph"
                                defaultMessage="Overview Graph"
                              />
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: 3
                          }}
                        ></Box>
                      </Box>

                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            aggregatedDataArray[aggregatedChartState]
                              .chartOverview.overview,
                            'overview',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            true
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.gridImportPower,
                            color: '#b30000',
                            type: 'bar'
                          },
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.gridExportPower,
                            color: '#2e7d32',
                            type: 'bar'
                          },
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.pvProduction,
                            type: 'bar',
                            color: '#ff9900'
                          },
                          ...((product !== 2 && product !== 5) ? [{
                            name: 'Net Energy',
                            color: '#e65100',
                            type: 'line',
                            data: aggregatedDataArray[aggregatedChartState]
                              .netbalance
                          }] : [])
                        ]}
                        height={400}
                        type={'bar'}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )}

              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="stretch"
                spacing={3}
              >
                {!(aggregatedData && (product === 2 || product === 5)) && (
                <Grid item md={6} xs={12}>
                  <Card
                    sx={{
                      overflow: 'visible',
                      marginTop: '30px',
                      marginBottom: '30px'
                    }}
                  >
                    <Box
                      sx={{
                        marginLeft: '20px'
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1" noWrap>
                            <FormattedMessage
                              id="battery_soc"
                              defaultMessage="Battery SOC (State Of Charge)"
                            />
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          pt: 3
                        }}
                      ></Box>
                    </Box>

                    {dailyData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview.soc,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.soc,
                            color: '#00ccff'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    )}

                    {aggregatedData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            aggregatedDataArray[aggregatedChartState]
                              .chartOverview.soc,
                            'weekly',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            true
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.minsoc,
                            color: '#69d2e7'
                          },
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.maxsoc,
                            color: '#008FFB'
                          }
                        ]}
                        type="bar"
                        height={400}
                      />
                    )}
                  </Card>
                </Grid>
                )}
                <Grid item md={(aggregatedData && (product === 2 || product === 5)) ? 12 : 6} xs={12}>
                  <Card
                    sx={{
                      overflow: 'visible',
                      marginTop: '30px',
                      marginBottom: '30px'
                    }}
                  >
                    <Box
                      sx={{
                        marginLeft: '20px'
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1" noWrap>
                            <FormattedMessage
                              id="battery_power"
                              defaultMessage={
                                dailyData ? 'Battery Power' : 'Battery Energy'
                              }
                            />
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          pt: 3
                        }}
                      ></Box>
                    </Box>

                    {dailyData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview.dcPower,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.dcPower,
                            color: '#00ccff'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    )}

                    {aggregatedData && currentUser.userType == UserType.admin && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            (product === 2 || product === 5)
                              ? aggregatedDataArray[aggregatedChartState]
                                  .chartOverview.dcPowerWithoutHeating
                              : aggregatedDataArray[aggregatedChartState]
                                  .chartOverview.dcPower,
                            'weekly',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            (product === 2 || product === 5)
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.dcChargingPower,
                            color: '#008FFB'
                          },
                          ...((product !== 2 && product !== 5) ? [{
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.heatingPower,
                            color: '#ff9900'
                          }] : []),
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.dcDischargingPower,
                            color: '#69d2e7'
                          }
                        ]}
                        type="bar"
                        height={400}
                      />
                    )}

                    {aggregatedData && currentUser.userType == UserType.client && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            aggregatedDataArray[aggregatedChartState]
                              .chartOverview.dcPowerWithoutHeating,
                            'weekly',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            true
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.dcChargingPower,
                            color: '#008FFB'
                          },

                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.dcDischargingPower,
                            color: '#69d2e7'
                          }
                        ]}
                        type="bar"
                        height={400}
                      />
                    )}
                  </Card>
                </Grid>
              </Grid>

              {dailyData && currentUser.userType == UserType.admin && (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={3}
                >
                  {(product !== 2 && product !== 5) && (
                  <Grid item md={6} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="battery_temperature"
                                defaultMessage="Battery Temperature"
                              />
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: 3
                          }}
                        ></Box>
                      </Box>
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview
                              .temperature,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.temperature,
                            color: '#00ccff'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    </Card>
                  </Grid>
                  )}
                  {(product !== 2 && product !== 5) && (
                  <Grid item md={6} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="dc_voltage"
                                defaultMessage="DC Bus Voltage"
                              />
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: 3
                          }}
                        ></Box>
                      </Box>
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview
                              .dcBusVoltage,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData
                              .dcBusVoltage,
                            color: '#00ccff'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    </Card>
                  </Grid>
                  )}
                </Grid>
              )}

              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="stretch"
                spacing={3}
              >
                <Grid item md={6} xs={12}>
                  <Card
                    sx={{
                      overflow: 'visible',
                      marginTop: '30px',
                      marginBottom: '30px'
                    }}
                  >
                    <Box
                      sx={{
                        marginLeft: '20px'
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1" noWrap>
                            <FormattedMessage
                              id="pv_production"
                              defaultMessage="PV Production"
                            />
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          pt: 3
                        }}
                      ></Box>
                    </Box>

                    {dailyData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview
                              .pvProduction,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData
                              .pvProduction,
                            color: '#ff9900'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    )}

                    {aggregatedData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            aggregatedDataArray[aggregatedChartState]
                              .chartOverview.pvProduction,
                            'weekly',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            true
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.pvProduction,
                            color: '#ff9900'
                          }
                        ]}
                        type="bar"
                        height={400}
                      />
                    )}
                  </Card>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Card
                    sx={{
                      overflow: 'visible',
                      marginTop: '30px',
                      marginBottom: '30px'
                    }}
                  >
                    <Box
                      sx={{
                        marginLeft: '20px'
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1" noWrap>
                            <FormattedMessage
                              id="grid_power"
                              defaultMessage={
                                dailyData ? 'Grid Power' : 'Grid Energy'
                              }
                            />
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          pt: 3
                        }}
                      ></Box>
                    </Box>
                    {dailyData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview.gridPower,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.gridPower,
                            color: '#ff3333'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    )}
                    {aggregatedData && (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            aggregatedDataArray[aggregatedChartState]
                              .chartOverview.gridPower,
                            'weekly',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            true
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.gridImportPower,
                            color: '#b30000'
                          },

                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.gridExportPower,
                            color: '#ff3333'
                          }
                        ]}
                        type="bar"
                        height={400}
                      />
                    )}
                  </Card>
                </Grid>
              </Grid>

              {aggregatedData && (product === 2 || product === 5) && (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={3}
                >
                  <Grid item md={12} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="ac_load_aggregated"
                                defaultMessage="AC Load Energy"
                              />
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            aggregatedDataArray[aggregatedChartState]
                              .chartOverview.ACLoad,
                            'weekly',
                            aggregatedDataArray[aggregatedChartState].datelist,
                            true
                          )
                        }}
                        series={[
                          {
                            ...aggregatedDataArray[aggregatedChartState]
                              .chartData.acLoad,
                            color: '#ff9900'
                          }
                        ]}
                        type="bar"
                        height={400}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )}

              {dailyData && (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={3}
                >
                  <Grid item md={(product === 2 || product === 5) ? 12 : 6} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="ac_load"
                                defaultMessage="AC Load"
                              />
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: 3
                          }}
                        ></Box>
                      </Box>

                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview.ACLoad,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.ACLoad,
                            color: '#ff9900'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    </Card>
                  </Grid>
                  {(product !== 2 && product !== 5) && (
                  <Grid item md={6} xs={12}>
                    <Card
                      sx={{
                        overflow: 'visible',
                        marginTop: '30px',
                        marginBottom: '30px'
                      }}
                    >
                      <Box
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" noWrap>
                              <FormattedMessage
                                id="dc_load"
                                defaultMessage="DC Load"
                              />
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            pt: 3
                          }}
                        ></Box>
                      </Box>

                      <ReactApexChart
                        options={{
                          ...getChartOptions(
                            dailyDataArray[chartState].chartOverview.DCLoad,
                            'daily',
                            [],
                            true
                          ),
                          chart: {
                            events: {
                              beforeZoom: (chartContext, options) => {
                                startZoom();
                                handleBeforeZoom(chartContext, options);
                              }
                            }
                          }
                        }}
                        series={[
                          {
                            ...dailyDataArray[chartState].chartData.DCLoad,
                            color: '#ff3333'
                          }
                        ]}
                        type="line"
                        height={400}
                      />
                    </Card>
                  </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Container>
    );
  };

  if (props.loading) {
    return null;
  }

  return <>{renderGraphs()}</>;
}

export default Overview;
